"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-10">
        <div className="h-4 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-10">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild className="bg-transparent hover:bg-transparent focus:bg-transparent data-[active=true]:bg-transparent rounded-md p-0">
              <Link to={item.href || "#"} className="relative text-foreground text-[0.8125rem] font-medium uppercase tracking-[0.12em] py-2 group">
                <span>{item.label}</span>
                <span className="absolute left-0 -bottom-1 h-px w-0 bg-primary transition-all duration-300 ease-out group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6">
        <div className="h-10 w-40 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-48 bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-36 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-6">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} onClick={onItemClick} data-index={i} className="group flex items-center gap-4 text-foreground">
          <span className="size-1.5 bg-primary shrink-0" />
          <span className="font-default text-3xl sm:text-4xl font-bold uppercase tracking-tight leading-none group-hover:text-primary transition-colors duration-300">
            {item.label}
          </span>
        </Link>)}
    </nav>;
}
export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="2464" id="navigation" className={`sticky top-0 z-50 w-full bg-background text-foreground transition-all duration-[400ms] ease-out ${scrolled ? "bg-background/70 backdrop-blur-xl border-b border-foreground/10 shadow-3xl" : "bg-background border-b border-transparent"}`}>
      <div className="w-full px-6 lg:px-10">
        <div className="flex items-center justify-between h-[72px] lg:h-[88px]">
          {}
          <div className="flex items-center gap-3">
            <Link to="/HomePage" className="flex items-center" aria-label="Naber AirConduct home">
              <img src="/assets/artifact_img_yp9dcqx8" alt="Logo" className="h-7 w-auto" />
            </Link>
            <span className="size-1.5 bg-primary shrink-0" aria-hidden="true" />
          </div>

          {}
          <div className="hidden lg:flex items-center justify-center flex-1">
            <MenuProvider menu_id="16">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex items-center gap-4 lg:gap-6">
            <div className="hidden lg:flex items-center gap-6">
              <span className="h-6 w-px bg-foreground/20" aria-hidden="true" />
              <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-6 text-[0.8125rem] font-semibold uppercase tracking-[0.12em] rounded-md shadow-md">
                <Link to="/ContactPage">Beratung anfragen</Link>
              </Button>
            </div>

            {}
            <div className="lg:hidden">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Menü öffnen" className="text-foreground hover:bg-foreground/10 hover:text-foreground rounded-md">
                    <Menu className="size-5" strokeWidth={1} />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="bg-background text-foreground border-l border-foreground/10 w-full sm:max-w-md p-0 flex flex-col">
                  <SheetTitle className="sr-only">Navigation</SheetTitle>

                  <div className="flex items-center justify-between h-[72px] px-6 border-b border-foreground/10">
                    <Link to="/HomePage" className="flex items-center" onClick={() => setMobileOpen(false)} aria-label="Naber AirConduct home">
                      <img src="/assets/artifact_img_yp9dcqx8" alt="Logo" className="h-7 w-auto" />
                    </Link>
                    <span className="size-1.5 bg-primary shrink-0" aria-hidden="true" />
                  </div>

                  <div className="flex-1 overflow-y-auto px-6 py-10">
                    <MenuProvider menu_id="16">
                      <MobileMenuConsumer onItemClick={() => setMobileOpen(false)} />
                    </MenuProvider>
                  </div>

                  <div className="p-6 border-t border-foreground/10">
                    <SheetClose asChild>
                      <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 text-[0.8125rem] font-semibold uppercase tracking-[0.12em] rounded-md shadow-md">
                        <Link to="/ContactPage">Beratung anfragen</Link>
                      </Button>
                    </SheetClose>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </section>;
}